import { titleCase } from 'change-case'
import {
  DragEventHandler,
  MouseEventHandler,
  useState,
  Fragment,
  useRef,
  useEffect,
} from 'react'
import {
  Button,
  kuiperClassNames,
  renderIcon,
  WithTooltip,
} from '@opallabs/kuiper'
import { doNothing } from '../../Content/helpers/functions/doNothing'
import { useModuleSectionDataFormatter } from '../../Content/helpers/hooks'
import {
  GetContentByIDV2Return,
  LinkWithData,
  OnShowAssetPickerProps,
} from '../../Content/helpers/types'
import type {
  Module,
  ModuleDefinition,
  OnLinkDataUpdate,
  SectionPostOptions,
} from '../../Content/helpers/types'
import { ModularSectionOptions } from '../../ModularSectionOptions'
import type { SectionOptionProps } from '../../ModularSectionOptions/types'
import { styles } from './styles'
import { validations } from './validations'
import type { ImageTypesProps } from './validations'

const onDragOver: DragEventHandler = (e) => e.preventDefault()
const onDragStart: DragEventHandler = () => {
  return false
}

const POPOVER_WINDOW_MARGIN = 12

enum AssetLibraryTooltip {
  AVAILABLE = 'Open Asset Library',

  LINKPREVIEW = 'The Asset Library is not available when the link section is present. You can edit the image directly in the post or delete the link section.',
}

enum UploadAssetTooltip {
  AVAILABLE = 'Upload Assets',

  LINKPREVIEW = 'Uploading Assets is not available when the link section is present. You can edit the image directly in the post or delete the link section.',
}

interface Props {
  allProps: Record<string, unknown>
  availableModules: ModuleDefinition[]
  draggedComp: Module | null
  dragZone: number | null
  getContentByIdV2?: (id: number) => Promise<GetContentByIDV2Return>
  getModule?: (
    sectionPostOptions: SectionPostOptions,
    module: Module
  ) => SectionPostOptions
  index: number
  isDragging: boolean
  isPresenting: boolean
  module: Module
  modules: Module[]
  onDeleteSection: (index: number) => void
  onDrag: (e: MouseEvent, module: Module) => void
  onDragEnter: MouseEventHandler
  onDragLeave: MouseEventHandler
  onExpandAsset: (assetIndex: number) => void
  onRemoveAsset: (assetIndex: number, index: number) => void
  onShowAssetPicker: (args: OnShowAssetPickerProps) => void
  onTooltipToggle: (index: number) => void
  readOnly: boolean
  tooltipActive: boolean
  updateCoverAsset: (
    assetData: FileList,
    assetId: number,
    index: number
  ) => void
  updateLinkData: OnLinkDataUpdate
  updateSectionOptions: (index: number, value: string, name: string) => void
  uploadAssets: (assets: FileList, args: OnShowAssetPickerProps) => void
  width: number
}

export const ModuleSection: React.FC<Props> = ({
  allProps,
  availableModules,
  draggedComp,
  dragZone,
  getContentByIdV2,
  getModule,
  index,
  isDragging,
  isPresenting,
  module,
  modules,
  onDeleteSection,
  onDrag,
  onDragEnter,
  onDragLeave,
  onExpandAsset,
  onRemoveAsset,
  onShowAssetPicker,
  onTooltipToggle,
  readOnly,
  tooltipActive,
  updateCoverAsset,
  updateLinkData,
  updateSectionOptions,
  uploadAssets,
  width,
}) => {
  const { assets, content, sectionPostOptions, sectionOptions } =
    useModuleSectionDataFormatter(module)
  const [isHovering, setIsHovering] = useState(false)
  const popoverBodyRef = useRef<HTMLDivElement>(null)
  const [popoverBodyRefHeight, setPopoverBodyRefHeight] = useState(0)
  const [popoverOverflowX, setPopoverOverflowX] = useState(0)

  const isLastSection = index === modules.length - 1 && index !== 0
  const matchingLink =
    allProps.linksWithData &&
    (allProps.linksWithData as LinkWithData[]).find((link) =>
      content.includes(link.url)
    )
  const dragState = isDragging
  const mappedComponent = availableModules.find(
    (mod) =>
      mod.componentName === module.componentName ||
      mod.componentName === module.name
  )
  const Component = mappedComponent?.component
  const moduleSectionOptions = getModule
    ? getModule(sectionPostOptions, module)
    : {}

  const toggleTooltip = () => {
    const newTooltipStatus = !tooltipActive
    const tooltipStatusIndex = newTooltipStatus ? index : -1
    onTooltipToggle(tooltipStatusIndex)
  }

  const onCloseTooltip = (): void => {
    toggleTooltip()
    setIsHovering(false)
  }

  const deleteSection = (index: number): void => {
    onCloseTooltip()
    onDeleteSection(index)
  }

  const handleMouseDown = (e: MouseEvent) => {
    onDrag(e, module)
    onTooltipToggle(-1)
  }

  const handleFileUpload = (files: FileList) => {
    const componentName = module.componentName || ''
    const imgValidations: ImageTypesProps | null =
      validations.imageValidations[componentName] || null
    if (imgValidations) {
      const filesArr: File[] = []
      for (let i = 0; i < files.length; i++) {
        filesArr.push(files[i])
      }
      const images = filesArr.filter((file) => file.type.includes('image'))
      const videos = filesArr.filter((file) => file.type.includes('video'))
      const gifs = filesArr.filter((file) => file.type.includes('gif'))
      if (
        images.length > imgValidations.image_max ||
        videos.length > imgValidations.video_max ||
        gifs.length > imgValidations.gif_max
      ) {
        return
      }
    }

    uploadAssets(files, { destination: 'post_component', index: index })
  }

  const onSectionOptionsSave = (options: SectionOptionProps[]) => {
    onCloseTooltip()
    const desiredIndex =
      options[0] && options[0].selected
        ? +options[0].selected?.title - 1
        : index
    options.forEach((option) => {
      const optionName = option.option.toLowerCase()
      if (optionName === 'section position') {
        updateSectionOptions(index, desiredIndex.toString(), optionName)
      } else if (option.type === 'select') {
        updateSectionOptions(
          desiredIndex,
          option.selected?.title || '',
          optionName
        )
      } else {
        updateSectionOptions(
          desiredIndex,
          (option.content || '') as string,
          optionName
        )
      }
    })
  }

  useEffect(() => {
    if (tooltipActive || popoverBodyRefHeight === 0) {
      setPopoverBodyRefHeight(popoverBodyRef.current?.offsetHeight || 0)
    } else {
      setPopoverBodyRefHeight(0)
    }
  }, [tooltipActive, popoverBodyRefHeight])

  // The sidebar sits to the right of the post preview, so on a narrow window
  // the popover opens past the edge of the screen. Slide it back by however
  // much it overflows. The measured edge already includes the current slide,
  // so adding it back gives the unslid position and the result settles.
  useEffect(() => {
    if (!tooltipActive) return
    const popoverRight = popoverBodyRef.current?.getBoundingClientRect().right
    if (popoverRight === undefined) return
    setPopoverOverflowX(
      Math.max(
        0,
        popoverRight +
          popoverOverflowX +
          POPOVER_WINDOW_MARGIN -
          window.innerWidth
      )
    )
  }, [tooltipActive, popoverOverflowX])

  /* eslint-disable  jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
  return (
    <Fragment>
      {Component && mappedComponent && (
        <div
          css={styles.moduleWrapperStyle}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <div
            className={kuiperClassNames('draggable-area')}
            css={styles.getDragAreaStyles(width, dragState, dragZone === index)}
            data-zone-id={index}
            onMouseEnter={onDragEnter}
            onMouseLeave={onDragLeave}
            onDragOver={onDragOver}
          />
          <div css={styles.moduleWrapperStyle}>
            {dragState && draggedComp && draggedComp.position !== index && (
              <div css={styles.getDragOverlay(width)} />
            )}
            <Component
              {...allProps}
              {...moduleSectionOptions}
              assets={assets || []}
              content={content}
              index={index}
              linksWithData={allProps.linksWithData as LinkWithData[]}
              modules={modules}
              getContentByIdV2={getContentByIdV2 ? getContentByIdV2 : doNothing}
              onExpandAsset={onExpandAsset}
              onRemoveAsset={(assetId: number) => onRemoveAsset(assetId, index)}
              onShowAssetPicker={(args: OnShowAssetPickerProps) =>
                onShowAssetPicker({ ...args, index })
              }
              postType={(allProps.postType as string) || ''}
              updateCoverAsset={(files: FileList, assetId: number) =>
                updateCoverAsset(files, assetId, index)
              }
              updateLinkData={updateLinkData}
              updateText={(value: string, name: string) =>
                updateSectionOptions(
                  index,
                  value,
                  titleCase(name).toLocaleLowerCase()
                )
              }
            />
          </div>
          {index === modules.length - 1 && (
            <div
              className={kuiperClassNames('draggable-area')}
              css={styles.getFinalDragAreaStyles(
                width,
                dragState,
                dragZone === modules.length
              )}
              data-zone-id={index + 1}
              onDragOver={onDragOver}
              onMouseEnter={onDragEnter}
              onMouseLeave={onDragLeave}
            />
          )}
          {!readOnly && !isPresenting && (
            <div
              className={kuiperClassNames('modular-section-sidebar-options')}
              css={styles.getSidebarWrapperStyle(
                width,
                isHovering || tooltipActive || dragState
              )}
            >
              {assets !== null && (
                <div
                  css={styles.getGroupedSidebarButtonWrapper(
                    Boolean(matchingLink)
                  )}
                >
                  <WithTooltip
                    tooltipOptions={{ position: 'top' }}
                    content={
                      matchingLink
                        ? AssetLibraryTooltip.LINKPREVIEW
                        : AssetLibraryTooltip.AVAILABLE
                    }
                  >
                    <Button
                      ariaLabel="Add Section Assets from Asset Library"
                      onClick={(): void =>
                        onShowAssetPicker({
                          destination: 'post_component',
                          index,
                        })
                      }
                      disabled={!!matchingLink}
                      className={kuiperClassNames(
                        'modular-section-asset-picker-asset-library'
                      )}
                      icon="asset-library"
                      style={styles.groupedSidebarButton}
                    />
                  </WithTooltip>
                  <div
                    className={kuiperClassNames(
                      'modular-section-asset-picker-divider'
                    )}
                  />
                  <WithTooltip
                    tooltipOptions={{ position: 'top' }}
                    content={
                      matchingLink
                        ? UploadAssetTooltip.LINKPREVIEW
                        : UploadAssetTooltip.AVAILABLE
                    }
                  >
                    <div
                      css={styles.fileUploadStyle}
                      className={kuiperClassNames(
                        'modular-section-asset-picker-asset-uploader'
                      )}
                    >
                      <div>
                        <label
                          htmlFor={`kuiper-modular-section-file-input-${index}`}
                        >
                          {renderIcon('upload', {
                            width: 12,
                            height: 14,
                            ariaLabel: 'Upload Assets to Section Icon Trigger',
                          })}
                        </label>
                        <input
                          key={`modular-section-upload-${index}`}
                          aria-label="Section Assets Upload"
                          id={`kuiper-modular-section-file-input-${index}`}
                          type="file"
                          accept="image/*, video/*"
                          multiple={true}
                          disabled={Boolean(matchingLink)}
                          onChange={(ev) =>
                            handleFileUpload(ev.target.files as FileList)
                          }
                        />
                      </div>
                    </div>
                  </WithTooltip>
                </div>
              )}
              <WithTooltip
                tooltipOptions={{ position: 'top' }}
                content={'Reposition Section'}
              >
                <div
                  className={kuiperClassNames('modular-section-dnd-grabber')}
                  css={styles.getDragSidebarButton(width, false, false)}
                  onDragStart={onDragStart}
                  aria-grabbed={dragState}
                  onMouseDown={(e) =>
                    handleMouseDown(e as unknown as MouseEvent)
                  }
                >
                  {renderIcon('grabber-ns', {
                    height: 18,
                    width: 18,
                    ariaMode: 'presentational',
                    ariaLabel: 'Drag to Reorder Section Icon Trigger',
                  })}
                </div>
              </WithTooltip>
              <WithTooltip
                tooltipOptions={{ position: 'top' }}
                content={'Section Options'}
              >
                <div
                  css={styles.sidebarButton}
                  className={kuiperClassNames(
                    'modular-section-options-popover'
                  )}
                >
                  {tooltipActive && (
                    <div
                      ref={popoverBodyRef}
                      className={kuiperClassNames(
                        'modular-section-options-popover-body'
                      )}
                      css={styles.getPopoverStyling(
                        tooltipActive,
                        assets !== null,
                        isLastSection,
                        popoverBodyRefHeight,
                        popoverOverflowX
                      )}
                    >
                      <ModularSectionOptions
                        componentName={mappedComponent.title}
                        deleteDisabled={false}
                        index={index}
                        onExit={() => onTooltipToggle(-1)}
                        onSectionDelete={() => deleteSection(index)}
                        onSectionOptionsSave={(options: SectionOptionProps[]) =>
                          onSectionOptionsSave(options)
                        }
                        readOnly={false}
                        systemScheduledAt={allProps.systemScheduledAt as string}
                        timezone={allProps.timezone as string}
                        sectionOptions={sectionOptions}
                        sections={modules}
                      />
                    </div>
                  )}
                  <div css={styles.optionsStyling} onClick={toggleTooltip}>
                    {renderIcon('cog-lg', {
                      ariaLabel: 'Open Section Options Popover Icon Trigger',
                    })}
                  </div>
                </div>
              </WithTooltip>
            </div>
          )}
        </div>
      )}
    </Fragment>
  )
  /* eslint-enable  jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
}
